import { LLM_CONFIG, ModelConfig } from '../../config';
import { SceneData, LLMResponse } from '../../types';
import { BaseLLMAdapter } from './BaseLLMAdapter';
import { ResponseProcessor } from '../../processors/response';
import { ReasoningVisualizer } from '../../components/ReasoningVisualizer';
import { LLMDebugger } from '../../utils/LLMDebugger';

/**
 * Gemini LLM适配器实现，处理Gemini相关的特殊逻辑
 */
export class GeminiAdapter extends BaseLLMAdapter {
  private responseProcessor: ResponseProcessor;
  private reasoningVisualizer: ReasoningVisualizer;
  private debugger: LLMDebugger;
  
  /**
   
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    super(config, debugMode);
    this.responseProcessor = new ResponseProcessor(debugMode);
    this.reasoningVisualizer = ReasoningVisualizer.getInstance();
    this.debugger = LLMDebugger.getInstance();
    this.debugger.setDebugMode(debugMode);
  }
  
  /**
   * 准备Gemini请求
   */
  async prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }> {
    const endpoint = this.getEndpoint();
    const headers = this.createBaseHeaders();
    
    // 构建请求体
    const body = this.createBaseBody(prompt);
    
    // Gemini特殊配置
    body.stream = true;
    
    // 根据供应商设置不同的token限制参数
    if (this.config.vendor === '302ai') {
      body.max_tokens = LLM_CONFIG.maxTokens;
    } else {
      body.maxOutputTokens = LLM_CONFIG.maxOutputTokens;
    }
    
    // 添加系统消息
    const systemMessage = "你是一个赛博朋克2077风格游戏世界的创造者和叙事者。这是一个充满高科技和低生活的世界，霓虹与阴影并存，义体与人性交织。";
    
    // 根据供应商不同调整messages格式
    if (this.config.vendor === '302ai') {
      // 302ai的Gemini接口与OpenAI兼容
      body.messages = [
        {
          role: "system",
          content: systemMessage
        },
        {
          role: "user",
          content: prompt
        }
      ];
    } else {
      // 标准Gemini接口使用不同的格式
      body.messages = [
        {
          role: "user",
          parts: [
            { text: systemMessage + "\n\n" + prompt }
          ]
        }
      ];
    }
    
    // 显示推理可视化
    console.log('显示Gemini推理可视化');
    this.reasoningVisualizer.show(this.config.id, this.config.vendor || 'google');
    
    // 记录请求详情
    this.debugger.logModelCall(this.config.id, {
      endpoint,
      model: this.config.id,
      vendor: this.config.vendor || '未知',
      headers: Array.from(headers.entries()),
      body
    });
    
    return { endpoint, headers, body };
  }
  
  /**
   * 处理Gemini响应
   */
  async processResponse(response: Response): Promise<SceneData | null> {
    try {
      return await this.handleStreamResponse(response);
    } catch (error) {
      // 记录错误并获取分析
      const errorAnalysis = await this.debugger.analyzeError(error as Error, this.config.id, {
        responseStatus: response.status,
        responseHeaders: Array.from(response.headers.entries())
      });
      
      // 隐藏推理可视化
      this.reasoningVisualizer.hide();
      
      // 抛出带有分析的错误
      throw new Error(`Gemini处理响应失败: ${(error as Error).message}\n\n${errorAnalysis}`);
    }
  }
  
  /**
   * 处理流式响应
   */
  private async handleStreamResponse(response: Response): Promise<SceneData | null> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let accumulatedContent = '';
    let reasoningContent = '';
    let lastJsonContent = '';
    let completeJsonFound = false;
    
    this.logDebug(`开始处理流式响应: 模型=${this.config.id}`, {});
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        // 将Uint8Array转换为字符串
        const chunk = new TextDecoder().decode(value);
        
        // 记录流式响应片段
        this.debugger.logStreamChunk(this.config.id, chunk);
        
        // 处理SSE格式的数据
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && !line.includes('[DONE]')) {
            try {
              const jsonStr = line.substring(6); // 去掉 "data: "
              const jsonData = JSON.parse(jsonStr);
              
              this.logDebug('解析后的JSON数据:', jsonData);
              
              // 根据供应商和响应格式不同进行处理
              let content = '';
              
              if (this.config.vendor === '302ai') {
                // 302ai的Gemini使用与OpenAI相同的响应格式
                if (jsonData.choices?.[0]?.delta?.content) {
                  content = jsonData.choices[0].delta.content;
                }
              } else {
                // 标准Gemini的响应格式
                if (jsonData.candidates?.[0]?.content?.parts?.[0]?.text) {
                  content = jsonData.candidates[0].content.parts[0].text;
                } else if (jsonData.choices?.[0]?.delta?.content) {
                  // 兼容标准OpenAI格式
                  content = jsonData.choices[0].delta.content;
                }
              }
              
              // 累积内容
              if (content) {
                accumulatedContent += content;
                reasoningContent += content;
                
                // 更新推理可视化
                this.reasoningVisualizer.update(reasoningContent);
                
                // 尝试寻找完整的JSON结构
                if (!completeJsonFound) {
                  // 检查当前累积内容是否包含完整的JSON结构
                  const jsonMatch = accumulatedContent.match(/```json\s*\n([\s\S]*?)\n\s*```/) || 
                                   accumulatedContent.match(/\{\s*"scene_text"[\s\S]*?"choices"\s*:\s*\[[\s\S]*?\]\s*\}/);
                  
                  if (jsonMatch) {
                    lastJsonContent = jsonMatch[1] || jsonMatch[0];
                    
                    try {
                      // 尝试解析JSON
                      JSON.parse(lastJsonContent);
                      completeJsonFound = true;
                      this.logDebug('找到完整的JSON结构', { jsonLength: lastJsonContent.length });
                    } catch (e) {
                      // 不是有效的JSON，继续累积
                    }
                  }
                }
              }
            } catch (error) {
              this.debugger.logModelResponse(this.config.id, null, error as Error);
            }
          }
        }
      }
      
      this.logDebug('累积内容:', accumulatedContent);
      
      // 处理完成，隐藏推理可视化
      setTimeout(() => {
        this.reasoningVisualizer.hide();
      }, 1000);
      
      // 如果找到了完整的JSON结构，优先使用它
      if (completeJsonFound && lastJsonContent) {
        try {
          const sceneData = this.extractValidSceneData(lastJsonContent);
          if (sceneData) {
            this.debugger.logModelResponse(this.config.id, sceneData);
            return sceneData;
          }
        } catch (e) {
          this.debugger.logModelResponse(this.config.id, null, e as Error);
        }
      }
      
      // 将累积的内容处理为SceneData
      const mockResponse: LLMResponse = {
        choices: [
          {
            message: {
              content: accumulatedContent
            }
          }
        ]
      };
      
      const result = await this.responseProcessor.processResponse(mockResponse);
      this.debugger.logModelResponse(this.config.id, result);
      return result;
    } catch (error) {
      this.reasoningVisualizer.hide();
      this.debugger.logModelResponse(this.config.id, null, error as Error);
      throw error;
    }
  }

  /**
   * 从JSON内容中提取有效的场景数据
   * 即使部分选项不完整，也尽可能提取有效的选项
   */
  private extractValidSceneData(jsonContent: string): SceneData | null {
    try {
      let data = JSON.parse(jsonContent);
      
      // 如果数据被包裹在choices数组中，提取实际的场景数据
      if (Array.isArray(data?.choices?.[0]?.message?.content)) {
        data = data.choices[0].message.content;
      } else if (typeof data?.choices?.[0]?.message?.content === 'string') {
        try {
          data = JSON.parse(data.choices[0].message.content);
        } catch (e) {
          // 如果解析失败，保持原样
        }
      }
      
      // 验证基本结构
      if (!data.scene_text || !Array.isArray(data.choices)) {
        return null;
      }
      
      // 过滤出有效的选项
      const validChoices = data.choices.filter((choice: any) => {
        return (
          choice &&
          typeof choice.choice_text === 'string' && choice.choice_text.trim() !== '' &&
          typeof choice.next_scene_id === 'string' && choice.next_scene_id.trim() !== ''
        );
      });
      
      // 如果没有有效选项，返回null
      if (validChoices.length === 0) {
        return null;
      }
      
      // 构造有效的场景数据
      const sceneData: SceneData = {
        scene_text: data.scene_text,
        choices: validChoices
      };
      
      // 可选字段的处理
      if (data.scene_id) {
        sceneData.scene_text = data.scene_id;
      }
      
      if (data.background_image) {
        // 可以添加到 scene_text 中或者其他适当的字段
        sceneData.scene_text += `\n[背景图片: ${data.background_image}]`;
      }
      
      if (data.background_music) {
        // 可以添加到 scene_text 中或者其他适当的字段
        sceneData.scene_text += `\n[背景音乐: ${data.background_music}]`;
      }
      
      this.logDebug('提取的有效场景数据:', {
        originalChoicesCount: data.choices.length,
        validChoicesCount: validChoices.length,
        sceneData
      });
      
      return sceneData;
    } catch (e) {
      this.debugger.logModelResponse(this.config.id, null, e as Error);
      return null;
    }
  }
} 